import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import {
  ChatClosedView,
  ChatEditor,
  ChatInfoView,
  ChatMini,
  ChatQueueView,
  ChatRequestEditor,
} from './components'
import { ChatDisplayState, ChatLine, ChatState } from '../../models/chat'
import {
  beforeCloseWaring,
  chatQuit,
  chatStartedSuccess,
  initializeApi,
  minimize,
  openChatForm,
  requestChat,
  sendMessage,
  showOpeningHours,
} from '../../store/actions/chat-actions'
import { getText } from '../../internal/TextLabels'
import { Roles } from '../../models/roles'
import {
  getErrandTypes,
  getLocalizedCreditSupport,
  isSelectedChatSupportOpen,
} from '../../models/contactInformation'
import { Countries } from '../../models/countries'
import { sendChatImpressionEvent } from '../../util/ga-helpers'
import { ChatOpeningHours } from './components/ChatOpeningHours'

import styles from './chat.module.scss'
import { ChatBeforeCloseView } from './components/ChatBeforeCloseView'
import { Features, isEnabled } from '../../models/features'

const ChatContainer = ({ chat, auth, router, storeInfo, quit, ...props }) => {
  const countryCode = storeInfo.currentStore?.country || Countries.SE
  const creditSupportOpeningHours = getLocalizedCreditSupport(countryCode)
  const [country] = useState(
    storeInfo && storeInfo.currentRepresentative
      ? storeInfo.currentRepresentative.country
      : Countries.SE,
  )
  const [errands] = useState(
    getErrandTypes(
      getText('chat.request.errandType.storeLabel'),
      getText('chat.request.errandType.terminalLabel'),
      getText('chat.request.errandType.materialLabel'),
      getText('chat.request.errandType.paymentsLabel'),
      getText('chat.request.errandType.creditLabel'),
    )
      .map(e => e[country])
      .filter(t => t),
  )

  useEffect(() => {
    if (typeof window.CallGuide !== 'undefined') {
      props.initializeApi()
    }
  }, [typeof window.CallGuide])

  const getChatTitle = () => {
    const errand = errands.find(type => type.value === chat.errand)
    if (errand) {
      return errand.title
    }
    return 'Chat'
  }

  const getDefaultErrandType = () => {
    let defaultValue = getText('chat.request.errandDropDownText')
    if (chat.errand !== '') {
      return chat.errand
    }
    const location = router.location.pathname.substring(1)
    if (location.length === 0) {
      return defaultValue
    }

    errands.forEach(type => {
      const defaults = type.defaultFor.split(',')
      defaults.forEach(d => {
        if (d !== '' && location.indexOf(d.trim()) > -1) {
          defaultValue = type.value
        }
      })
    })
    return defaultValue
  }

  const isChatServiceOpen = () => {
    if (chat.chatState === ChatState.ONGOING) {
      return true
    }

    return Object.values(ChatLine).some(type => {
      return isSelectedChatSupportOpen(country, type)
    })
  }

  const filterErrandTypesOnRole = () => {
    const userRole = auth.role
    return errands.filter(
      t =>
        t.allowedRoles === '*' ||
        t.allowedRoles.indexOf(userRole) > -1 ||
        userRole === Roles.RESURS_ADMIN,
    )
  }

  const getChatToDisplay = () => {
    switch (chat.displayState) {
      case ChatDisplayState.REQUEST_FORM:
        return (
          <ChatRequestEditor
            country={country}
            title={getText('chat.mini.title')}
            history={[
              {
                fromMe: false,
                text: (
                  <span style={{ whiteSpace: 'pre-line' }}>
                    {getText('chat.request.greeting.1')}
                    <br />
                    {getText('chat.request.greeting.2')}
                    <span>{` ${creditSupportOpeningHours.phoneNumber}`}</span>
                  </span>
                ),
              },
            ]}
            requestLabel={getText('chat.request.errandText')}
            requestDropDownText={getText('chat.request.errandDropDownText')}
            requestChoices={filterErrandTypesOnRole().map(item => ({
              ...item,
              valueLabel: item.title,
            }))}
            defaultChoice={getDefaultErrandType()}
            buttonText={getText('chat.request.buttonText')}
            onSend={props.requestChat}
            onClose={quit}
            onMinimize={props.minimize}
            message={chat.msg}
          />
        )
      case ChatDisplayState.CHAT_OPENED:
        return (
          <ChatEditor
            title={getChatTitle()}
            statusText={chat.servingAgent}
            history={chat.history}
            onSend={props.sendMessage}
            onClose={quit}
            onMinimize={props.minimize}
            buttonText={getText('chat.editor.sendButton')}
            penState={chat.penState}
            onBeforeCloseChat={props.beforeCloseChat}
          />
        )
      case ChatDisplayState.CHAT_IN_QUEUE:
        return (
          <ChatQueueView
            queuePos={chat.queueInfo.queuePos}
            estQueueTime={chat.queueInfo.estQueueTime}
            onClose={quit}
            onMinimize={props.minimize}
            title={getChatTitle()}
          />
        )
      case ChatDisplayState.CHAT_ENTRANCE_CLOSED: {
        return (
          <ChatInfoView
            title={getText('chat.info.closed.title')}
            onClose={quit}
            onMinimize={props.minimize}
            message={<ChatOpeningHours countryCode={country} />}
          />
        )
      }
      case ChatDisplayState.FINISHED:
        return (
          <ChatClosedView
            title={getText('chat.info.finished.title')}
            history={chat.history}
            onClose={quit}
            onMinimize={props.minimize}
            message={getText('chat.info.finished.message')}
          />
        )
      case ChatDisplayState.BEFORE_CLOSE:
        return (
          <ChatBeforeCloseView
            title={getText('chat.mini.title')}
            onClose={quit}
            onMinimize={props.minimize}
            onBeforeCloseChat={props.beforeCloseChat}
            onBackToStartedChat={props.backToStartedChat}
          />
        )
      case ChatDisplayState.MINIMIZED:
      default:
        return null
    }
  }

  // do not render chat if user is not logged in, is in select store phase or if Denmark chat isn't enabled
  if (
    !auth.isLoggedIn ||
    auth.selectStore ||
    !isEnabled(Features.ACTIVATED_CHAT_DK)
  ) {
    return null
  }

  const chatToDisplay = getChatToDisplay()

  return (
    <div className={styles.chat}>
      <ChatMini
        disabled={!!chatToDisplay}
        closed={!isChatServiceOpen()}
        onClick={() => {
          if (isChatServiceOpen()) {
            sendChatImpressionEvent()
            props.openChatForm()
          } else {
            props.showOpeningHours()
          }
        }}
      />
      {chatToDisplay}
    </div>
  )
}

ChatContainer.propTypes = {
  chat: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  storeInfo: PropTypes.object.isRequired,
  openChatForm: PropTypes.func.isRequired,
  showOpeningHours: PropTypes.func.isRequired,
  requestChat: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired,
  minimize: PropTypes.func.isRequired,
  beforeCloseChat: PropTypes.func.isRequired,
  quit: PropTypes.func.isRequired,
  initializeApi: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  chat: state.chat,
  auth: state.auth,
  router: state.router,
  storeInfo: state.storeInfo,
})

const mapDispatchToProps = dispatch => ({
  openChatForm: () => {
    dispatch(openChatForm())
  },
  showOpeningHours: () => {
    dispatch(showOpeningHours())
  },
  initializeApi: () => {
    dispatch(initializeApi())
  },
  requestChat: request => {
    dispatch(requestChat(request))
  },
  sendMessage: msg => {
    dispatch(sendMessage(msg))
  },
  quit: () => {
    dispatch(chatQuit())
  },
  minimize: (errand, msg) => {
    dispatch(minimize(errand, msg))
  },
  beforeCloseChat: () => {
    dispatch(beforeCloseWaring())
  },
  backToStartedChat: () => {
    dispatch(chatStartedSuccess())
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(ChatContainer)
